import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { getPresignedUrl } from '../../../helpers/storage';
import request from '../../../helpers/request';

export default function useSidebarViewers() {
  const [sidebarItems, setSidebarItems] = useState([]);

  const handleCloseSidebarItem = useCallback((id) => {
    setSidebarItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleOpenDocument = useCallback(async (doc) => {
    let documentUrl = null;

    if (doc.url?.startsWith('blob:')) {
      documentUrl = doc.url;
    } else if (doc.url) {
      documentUrl = doc.url;
    } else if (doc.path) {
      try {
        const { data } = await getPresignedUrl('download', doc.path);
        documentUrl = data.url;
      } catch (err) {
        console.error('Failed to get presigned URL for path:', doc.path, err);
        toast.error('Failed to load document');
        return;
      }
    }

    if (!documentUrl) {
      console.error('No valid document URL. doc:', doc);
      toast.error('No valid document URL');
      return;
    }

    const itemId = doc.id || doc.path || `doc-${Date.now()}`;
    const newDoc = {
      id: itemId,
      name: doc.name || doc.path?.split('/').pop() || 'Document',
      path: doc.path,
      url: documentUrl,
      type: doc.type,
    };

    setSidebarItems((prev) => {
      const without = prev.filter((item) => item.id !== itemId);
      return [{ id: itemId, type: 'document', data: newDoc }, ...without];
    });
  }, []);

  const handleOpenPolicy = useCallback(async (policyId, fieldKey) => {
    if (!policyId) return;

    setSidebarItems((prev) => {
      const exists = prev.find((item) => item.id === policyId);
      if (exists && !exists.loading) {
        return [{ ...exists, fieldKey }, ...prev.filter((item) => item.id !== policyId)];
      }
      if (exists) return prev;
      return [{ id: policyId, type: 'policy', data: null, loading: true, fieldKey }, ...prev];
    });

    try {
      const res = await request.get(`/api/v1/compliance/policy/${policyId}/`);
      setSidebarItems((prev) =>
        prev.map((item) =>
          item.id === policyId ? { ...item, data: res.data, loading: false } : item
        )
      );
    } catch {
      toast.error('Failed to load policy');
      setSidebarItems((prev) => prev.filter((item) => item.id !== policyId));
    }
  }, []);

  const handleOpenTask = useCallback(async (taskId) => {
    if (!taskId) return;

    const normalizedTaskId = String(taskId);
    const itemId = `task-${normalizedTaskId}`;

    setSidebarItems((prev) => {
      const exists = prev.find((item) => item.id === itemId);
      if (exists && !exists.loading) {
        return [exists, ...prev.filter((item) => item.id !== itemId)];
      }
      if (exists) return prev;
      return [{
        id: itemId,
        type: 'task',
        taskId: normalizedTaskId,
        data: { result: null, input: null },
        loading: true,
      }, ...prev];
    });

    try {
      const [resultRes, inputRes] = await Promise.allSettled([
        request.get(`/api/v1/tools/result/${normalizedTaskId}/`),
        request.get(`/api/v1/tools/task/${normalizedTaskId}/`),
      ]);

      const resultData = resultRes.status === 'fulfilled' ? resultRes.value.data : null;
      const inputData = inputRes.status === 'fulfilled' ? inputRes.value.data : null;

      if (!resultData && !inputData) {
        throw new Error('No task data loaded');
      }

      setSidebarItems((prev) =>
        prev.map((item) => (
          item.id === itemId
            ? {
              ...item,
              data: { result: resultData, input: inputData },
              loading: false,
            }
            : item
        ))
      );
    } catch (e) {
      console.error('Failed to load task result:', e);
      toast.error('Failed to load task result');
      setSidebarItems((prev) => prev.filter((item) => item.id !== itemId));
    }
  }, []);

  return {
    sidebarItems,
    handleCloseSidebarItem,
    handleOpenDocument,
    handleOpenPolicy,
    handleOpenTask,
  };
}
